import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLoader, isLiveNetwork } from '@/features/loader/store';
import {
  antennaFileForNetwork,
  missionFileForNetwork,
  loadStaticAntennas,
  loadStaticMissions,
} from '@/features/loader/data/staticData';
import type { Antenna, Mission } from '@/features/loader/types';
import { facilitiesForNetwork, facilityAssetId } from './facilities';

// These NASA catalogue rows reserve planning time; none identifies a spacecraft.
const NASA_PLANNING_ACTIVITIES = new Set([
  'tracking_cal', 'range_safety', 'spectrum_monitor', 'antenna_maintenance',
  'software_upgrades', 'calibration_activities', 'decommissioning',
]);

export interface CatalogueLeaf {
  id: string;
  name: string;
  description?: string;
  mission?: Mission;
  antenna?: Antenna;
}
export function useMaintenanceCatalogue(examples = false) {
  const selectedNetwork = useLoader((s) => s.network);
  const network = examples ? 'demo' : selectedNetwork;
  const dataset = useLoader((s) => s.dataset);
  const datasetStatus = useLoader((s) => s.datasetStatus);
  const datasetError = useLoader((s) => s.datasetError);
  const live = isLiveNetwork(network);
  const query = useQuery({
    queryKey: ['maintenance-catalogue', network],
    staleTime: Infinity,
    enabled: !live && network !== 'rfims' && network !== 'demo',
    queryFn: async () => {
      const mf = missionFileForNetwork(network),
        af = antennaFileForNetwork(network);
      const [missions, antennas] = await Promise.all([
        mf ? loadStaticMissions(mf) : Promise.resolve([]),
        af ? loadStaticAntennas(af) : Promise.resolve([]),
      ]);
      return { missions, antennas };
    },
  });
  const data = dataset?.network === network ? dataset : query.data;
  const groups = useMemo<Record<string, CatalogueLeaf[]>>(
    () => ({
      noaa: (data?.missions ?? [])
        .filter((m) => network !== 'nasa' || !NASA_PLANNING_ACTIVITIES.has(m.id))
        .map((m) => ({
          id: `mission:${encodeURIComponent(network)}:${encodeURIComponent(m.id)}`,
          name: m.name,
          description: m.catalogue
            ? `${m.catalogue.role}. ${m.catalogue.status}. ${m.catalogue.model}.`
            : m.description,
          mission: m,
        })),
      antennas: (data?.antennas ?? []).map((a) => ({
        id: `${network}:${a.id}`,
        name: a.name,
        description: `${a.location.name ?? 'Catalogue site'} · ${a.capabilities.diameterM ?? 'Unknown'} m · Representative equipment`,
        antenna: a,
      })),
      stations: facilitiesForNetwork(network).map((f) => ({
        id: facilityAssetId(f.id),
        name: f.name,
        description: `${f.operator} · ${f.role} · ${f.members.filter((m) => m.network === (network === 'noaa_extended' ? 'noaa' : network)).length} catalogue antennas`,
      })),
    }),
    [network, data],
  );
  return {
    network,
    data,
    groups,
    loading: !examples && (live ? !data && datasetStatus !== 'error' : query.isLoading),
    error: examples ? null : datasetStatus === 'error' ? datasetError : query.error?.message,
    retry: () => {
      useLoader.setState((s) => ({ datasetRetry: s.datasetRetry + 1 }));
      if (!live) void query.refetch();
    },
  };
}
